import { useEffect, useMemo, useState } from 'react';
import {
  getInvoices,
  getShops,
  downloadCsv,
  regenerateInvoices,
} from '../../services/adminMockData';

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('nl-NL', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

export default function AdminInvoicesPage() {
  const [invoices, setInvoices] = useState(() => getInvoices());
  const shops = useMemo(() => getShops(), []);
  const [shopFilter, setShopFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  useEffect(() => { setInvoices(getInvoices()); }, []);

  const months = useMemo(() => {
    const set = new Set(invoices.map((i) => i.date.slice(0, 7)));
    return Array.from(set).sort().reverse();
  }, [invoices]);

  const filtered = useMemo(() => invoices.filter((inv) => {
    if (shopFilter && inv.shopId !== shopFilter) return false;
    if (monthFilter && inv.date.slice(0, 7) !== monthFilter) return false;
    if (typeFilter && inv.type !== typeFilter) return false;
    return true;
  }), [invoices, shopFilter, monthFilter, typeFilter]);

  const summary = useMemo(() => {
    const thisMonth = new Date().toISOString().slice(0, 7);
    const monthInv = invoices.filter((i) => i.date.slice(0, 7) === thisMonth);
    const totalEx = monthInv.reduce((s, i) => s + i.amount, 0);
    const totalVat = monthInv.reduce((s, i) => s + i.vat, 0);
    const avg = monthInv.length ? Math.round((totalEx / monthInv.length) * 100) / 100 : 0;
    return {
      count: monthInv.length,
      totalEx,
      totalVat,
      avg,
    };
  }, [invoices]);

  const exportAll = () => downloadCsv(
    `walkin-facturen-${new Date().toISOString().slice(0, 10)}.csv`,
    invoices.map((inv) => ({
      nummer: inv.number,
      winkel: inv.shopName,
      type: inv.type,
      bedrag_ex: inv.amount,
      btw: inv.vat,
      totaal: inv.total,
      datum: inv.date.slice(0, 10),
      status: inv.status,
    }))
  );

  const exportPerShop = () => {
    const rows = shops.map((s) => {
      const sInv = invoices.filter((i) => i.shopId === s.id);
      return {
        winkel: s.shopName,
        aantal: sInv.length,
        totaal_ex: sInv.reduce((sum, i) => sum + i.amount, 0),
        totaal_btw: sInv.reduce((sum, i) => sum + i.vat, 0),
        totaal_incl: sInv.reduce((sum, i) => sum + i.total, 0),
      };
    });
    downloadCsv(`walkin-facturen-per-winkel.csv`, rows);
  };

  return (
    <div>
      <header className="admin-page-head">
        <div>
          <h1 className="admin-page-title">Facturen</h1>
          <p className="admin-page-sub">{invoices.length} facturen in totaal</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="admin-btn admin-btn--ghost" onClick={exportPerShop}>
            Export per winkelier
          </button>
          <button className="admin-btn admin-btn--orange" onClick={exportAll}>
            Export CSV
          </button>
        </div>
      </header>

      <div className="admin-grid" style={{ marginBottom: 18 }}>
        <div className="admin-stat">
          <span className="admin-stat__label">Omzet deze maand</span>
          <span className="admin-stat__value">€{summary.totalEx.toLocaleString('nl-NL')}</span>
          <span className="admin-stat__delta">ex BTW</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__label">BTW deze maand</span>
          <span className="admin-stat__value">€{summary.totalVat.toLocaleString('nl-NL')}</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__label">Aantal facturen</span>
          <span className="admin-stat__value">{summary.count}</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__label">Gem. orderwaarde</span>
          <span className="admin-stat__value">€{summary.avg.toFixed(2)}</span>
        </div>
      </div>

      <div className="admin-toolbar">
        <select className="admin-select" value={shopFilter} onChange={(e) => setShopFilter(e.target.value)}>
          <option value="">Alle winkeliers</option>
          {shops.map((s) => <option key={s.id} value={s.id}>{s.shopName}</option>)}
        </select>
        <select className="admin-select" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}>
          <option value="">Alle maanden</option>
          {months.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select className="admin-select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="">Alle types</option>
          <option value="Starter">Starter</option>
          <option value="Growth">Growth</option>
          <option value="Unlimited">Unlimited</option>
        </select>
        <button className="admin-btn admin-btn--ghost admin-btn--small" onClick={() => {
          setInvoices(regenerateInvoices());
        }}>
          Mock-data herstellen
        </button>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Nummer</th>
              <th>Winkelier</th>
              <th>Pakket</th>
              <th>Ex BTW</th>
              <th>BTW</th>
              <th>Totaal</th>
              <th>Datum</th>
              <th>Status</th>
              <th>Acties</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="admin-empty">Geen facturen gevonden</td></tr>
            )}
            {filtered.map((inv) => (
              <tr key={inv.id}>
                <td><span className="admin-mono">{inv.number}</span></td>
                <td>{inv.shopName}</td>
                <td>
                  <span className={`admin-badge admin-badge--${inv.type.toLowerCase()}`}>
                    {inv.type}
                  </span>
                </td>
                <td>€{inv.amount.toFixed(2)}</td>
                <td>€{inv.vat.toFixed(2)}</td>
                <td><strong>€{inv.total.toFixed(2)}</strong></td>
                <td>{formatDate(inv.date)}</td>
                <td>
                  <span className={`admin-badge admin-badge--${inv.status.toLowerCase()}`}>
                    {inv.status}
                  </span>
                </td>
                <td>
                  <button
                    className="admin-btn admin-btn--small admin-btn--ghost"
                    onClick={() => downloadCsv(`${inv.number}.csv`, [inv])}
                  >
                    Download
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
